import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { BackHandler, Platform } from 'react-native';

import {
  useResponsive,
  useSafeExpoNavigation,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { type HeaderIconItems, useHeaderLeft, useHeaderRight } from '../../../../../../components';
import { joinOrSignScreenHeaderButtonsMessages as messages } from './useJoinOrSignScreenHeaderButtons.messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A custom hook that renders custom navigation header controls and conditionally
 * toggles various navigation options to improve user experience during loading.
 */
export const useJoinOrSignScreenHeaderButtons = (
  params: UseJoinOrSignScreenHeaderButtonsParams,
) => {
  const { isLoading } = params;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  const navigation = useSafeNavigation();
  const expoNavigation = useSafeExpoNavigation();
  const router = useSafeRouter();

  const { currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Helpers ──────────────────────────────────────────────────────────

  const goBack = useCallback(() => {
    if (isExpoRouterEnabled) {
      if (router?.canDismiss()) {
        router.dismissAll();
        return;
      }

      router?.navigate('/');
      return;
    }

    navigation?.goBack();
  }, [isExpoRouterEnabled, router, navigation]);

  // ─── Header Controls ─────────────────────────────────────────────────

  const headerLeftItems = useMemo<HeaderIconItems>(() => {
    // NOTE: On iOS, we render the "Join or Sign In" screen as a modal,
    //       therefore, rendering back button is not needed.
    if (Platform.OS === 'ios') return [];

    return [
      {
        key: 'join-or-sign-in-screen-header-back-btn',
        icon: 'IconArrowLeft',
        accessibilityLabel: formatMessage(messages.backBtnLabel),
        onPress: goBack,
      },
    ];
  }, [formatMessage, goBack]);

  const headerRightItems = useMemo<HeaderIconItems>(
    () => [
      {
        key: 'join-or-sign-in-screen-header-close-btn',
        icon: 'IconClose',
        accessibilityLabel: formatMessage(messages.closeBtnLabel),
        onPress: goBack,
      },
    ],
    [formatMessage, goBack],
  );

  // ─────────────────────────────────────────────────────────────────────

  const shouldHideControls = !currentBreakpoint.isXS || isLoading;

  const headerLeftXS = useHeaderLeft({ items: headerLeftItems });
  const headerRightXS = useHeaderRight({ items: headerRightItems });

  const headerLeft = shouldHideControls ? renderNoHeaderLeft : headerLeftXS;
  const headerRight = shouldHideControls ? undefined : headerRightXS;

  // ─── Effects ─────────────────────────────────────────────────────────

  /**
   * Conditionally disable hardware back button on Android.
   */
  useEffect(() => {
    const { remove: removeListener } = BackHandler.addEventListener(
      'hardwareBackPress',
      () => isLoading,
    );

    return removeListener;
  }, [isLoading]);

  /**
   * Set header controls.
   */
  useEffect(() => {
    const setOptions = navigation?.setOptions ?? expoNavigation?.setOptions;

    setOptions?.({
      headerLeft,
      headerRight,

      // NOTE: We use a custom back button, thus we need to hide the default one.
      headerBackVisible: false,
    });
  }, [headerLeft, headerRight, isLoading, navigation, expoNavigation]);

  /**
   * Conditionally enable "Swipe to close" functionality.
   */
  useEffect(() => {
    const parentStack = navigation?.getParent() ?? expoNavigation?.getParent();

    parentStack?.setOptions({
      gestureEnabled: !isLoading,
    });
  }, [expoNavigation, isLoading, navigation]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A no-op headerLeft renderer that prevents the default `HeaderBackButton`
 * from mounting. `HeaderBackButton` from `@react-navigation/elements` v2
 * calls `useLocale` which doesn't exist in `@react-navigation/native` v6.
 */
function renderNoHeaderLeft() {
  return null;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseJoinOrSignScreenHeaderButtonsParams = {
  isLoading: boolean;
};
